import React, { useId, useState } from 'react';
import { Alert, Button, Checkbox, Drawer, Empty, Input, Modal, Progress, Steps, Table, Tabs, Tag, Timeline, Tooltip, Typography } from 'antd';
import { CheckCircleFilled, ClockCircleOutlined, ExclamationCircleFilled, InfoCircleOutlined, PauseCircleOutlined, WarningOutlined } from '@ant-design/icons';

import { STAGE } from '@lfoc/domain/business-day/stages.js';

import { useAccess } from '../auth/AccessContext.jsx';
import ActionIcon from '../components/ActionIcon.jsx';
import { CenteredLoader } from '../components/Loader.jsx';
import StatusTag from '../components/StatusTag.jsx';
import { formatUnits } from '../format.js';
import { STATUS_KIND, statusHelp } from '../status/statusHelp.js';
import { useBusinessDay } from './BusinessDayContext.js';
import {
    REASON_MAX, RUN_DAY_END_HINT, TONE, advanceBody, advanceHint, advanceOutcome, advanceShown, checklistRows, dayEndCheckText, dayEndPanel, dayLong,
    dayTabs, eventItems, forceOptions, forcedItems, hiddenDaysText, openDayText, pauseText, rankIn, reasonProblem, dayEndWarningsText, runDayEndOutcome, runDayEndRefusal,
    runDayEndState, stageLabel, stagesOf, statementCell, stepItems, toneOf, whenText
} from './businessDay.js';
import { StepUpCancelled, useStepUp } from '../stepup/useStepUp.js';
import { GOLD } from '../config/palette.js';

const { Text } = Typography;

/** A stage's tag: open, processed, closed - the chip's dot in the drawer's own palette. */
const TONE_COLOUR = Object.freeze({ [TONE.OPEN]: 'default', [TONE.PROCESSING]: 'gold', [TONE.CLOSED]: 'green', [TONE.UNKNOWN]: 'default' });

/** A NAV run's state, as Unit Prices colours it (unitPrices/runs.js). */
const NAV_COLOUR = Object.freeze({ CHECKED: 'blue', QUARANTINED: 'red', APPROVED: 'gold', PUBLISHED: 'green', REJECTED: 'default', SUPERSEDED: 'default' });
const titleCase = (value) => String(value ?? '').toLowerCase().replace(/(^|_)(\w)/g, (m, sep, c) => (sep ? ' ' : '') + c.toUpperCase());
const dash = <Text type="secondary">—</Text>;
const countOrDash = (n) => (n === null || n === undefined ? dash : n);

/** A fund's statement: Loaded only when every collection account's is in; the ones still missing on hover. */
const StatementCell = ({ fund }) => {
    const { text, missing } = statementCell(fund);
    if (!missing.length || text === 'Loaded') return text;
    return (
        <Tooltip title={'Not loaded: ' + missing.join(', ')}>
            <span tabIndex={0} className="business-day-statement is-missing">{text}</span>
        </Tooltip>
    );
};

/**
 * The day's funds: its book, its statements per collection account, NAV run,
 * published price, the orders it still has to deal and the subscriptions
 * still waiting for their money (design 5.6 funds[]).
 */
const fundColumns = (day) => [
    { key: 'fundId', title: 'Fund', dataIndex: 'fundId' },
    {
        key: 'book',
        title: 'Book',
        render: (_, f) => (f.book ? <StatusTag kind={STATUS_KIND.BOOK} code={f.book.state} color={f.book.state === 'CLOSED' ? 'green' : 'gold'} label={titleCase(f.book.state)} /> : dash)
    },
    { key: 'statement', title: 'Statement', render: (_, f) => <StatementCell fund={f} /> },
    { key: 'navRun', title: 'NAV Run', render: (_, f) => (f.navRun ? <StatusTag kind={STATUS_KIND.NAV_RUN} code={f.navRun.state} color={NAV_COLOUR[f.navRun.state] ?? 'default'} label={titleCase(f.navRun.state)} /> : dash) },
    { key: 'price', title: 'Price Published', render: (_, f) => (f.pricePublishedAt ? whenText(f.pricePublishedAt, day.businessDate) : dash) },
    { key: 'pending', title: 'Pending Transactions', dataIndex: 'pendingOrders' },
    { key: 'awaiting', title: 'Awaiting Funds', render: (_, f) => countOrDash(f.awaitingFunds) }
];

/**
 * Day end's figures per fund (design 5.3 summary.funds), in two tables that
 * each fit the drawer: what became of the day's transactions, then the units
 * and the day's prices.
 */
const summaryOrderColumns = [
    { key: 'fundId', title: 'Fund', dataIndex: 'fundId' },
    { key: 'allocated', title: 'Allocated', dataIndex: 'allocated' },
    { key: 'rejected', title: 'Rejected', dataIndex: 'rejected' },
    { key: 'cancelled', title: 'Cancelled', dataIndex: 'cancelled' },
    { key: 'expired', title: 'Expired', dataIndex: 'expired' },
    { key: 'rolled', title: 'Rolled, Not Reviewed', dataIndex: 'rolledNotReviewed' },
    // The subscriptions the cut-off rolled on to the next day, their money not in (reads.js summaryOf fills it for an older summary).
    { key: 'rolledUnfunded', title: 'Rolled, Unfunded', render: (_, f) => countOrDash(f.rolledUnfunded) }
];

const priceOf = (f, field) => f.price?.[field] ?? dash;
const summaryPriceColumns = [
    { key: 'fundId', title: 'Fund', dataIndex: 'fundId' },
    { key: 'units', title: 'Units In Issue', render: (_, f) => (f.unitsInIssue === null || f.unitsInIssue === undefined ? dash : formatUnits(f.unitsInIssue)) },
    { key: 'nav', title: 'NAV per Unit', render: (_, f) => priceOf(f, 'navPerUnit') },
    { key: 'selling', title: 'Selling', render: (_, f) => priceOf(f, 'sellingPrice') },
    { key: 'buying', title: 'Buying', render: (_, f) => priceOf(f, 'buyingPrice') },
    { key: 'exitFee', title: 'Buying With Exit Fee', render: (_, f) => priceOf(f, 'buyingPriceWithExitFee') }
];

/** Day end's other figures in a line: the statements, the gaps, the aged receipts (books/checks.js), and the Day end process's own check. */
function summaryNote(summary) {
    const statements = summary.statements ?? [];
    const parts = [statements.filter((s) => s.loaded).length + ' of ' + statements.length + ' statements loaded'];
    if (Array.isArray(summary.statementGaps)) parts.push(summary.statementGaps.length + ' statement gap' + (summary.statementGaps.length === 1 ? '' : 's'));
    if (summary.agedReceipts) parts.push('aged receipts: ' + summary.agedReceipts.escalate + ' to escalate, ' + summary.agedReceipts.returnDue + ' due for return');
    const check = dayEndCheckText(summary);
    if (check) parts.push(check);
    return parts.join(' · ');
}

/**
 * IP-200: the Day End panel of a day the Day end process owns, from
 * Reconciliation & approvals on (dayEndPanel) - its status, who started it,
 * its progress while it runs, what to do when it stopped. Everyone who sees
 * the drawer sees it; Run day end below starts the run.
 */
const DayEndStatus = ({ view, day }) => {
    const panel = dayEndPanel(day, stagesOf(view));
    if (!panel.show) return null;
    return (
        <div className="business-day-dayend">
            <div className="business-day-dayend-head">
                <span className="business-day-dayend-title">Day End</span>
                <Tag color={panel.tag.colour}>{panel.tag.label}</Tag>
            </div>
            {panel.lines.map((line) => <p key={line} className="business-day-hint">{line}</p>)}
            {panel.progress && <Progress percent={panel.progress.percent} size="small" status="active" aria-label="Holders checked" />}
        </div>
    );
};

/**
 * IP-195 (owner, 17 Oct 2026): "a button to execute the day end process with approved
 * transactions". DAY_END_RUN starts the Day end process for this day now, with an
 * authenticator code: the day's priced transactions nobody approved are carried over when
 * reconciled with the bank statement, and moved to the next dealing date when not; then every
 * holding is checked and the day closed. The code's summary lists which transactions carry
 * over and which move, so the person sees it before confirming. Shown to whoever holds
 * core.book.close (runDayEnd), as Advance; the server checks it again.
 *
 * IP-200: shown on a day the Day end process owns from Reconciliation & approvals until it
 * closes (runDayEndState), "Run day end again" after a stop or a failure; held while a run is
 * going or an earlier day is open - the server refuses both, and its refusal is said the same way.
 */
const DayEndRun = ({ view, day, onStarted }) => {
    const { api, canAct } = useAccess();
    const { withStepUp, stepUpDialog } = useStepUp();
    const [busy, setBusy] = useState(false);
    const [outcome, setOutcome] = useState(null);
    // IP-203: the confirmation asked first when reconciled transactions are not approved yet.
    const [confirming, setConfirming] = useState(false);
    if (!canAct('businessDay', 'runDayEnd')) return null;
    const { show, label, enabled, reason } = runDayEndState(day, { view });
    if (!show) return null;
    const warnings = dayEndWarningsText(day);

    const run = async () => {
        setBusy(true);
        setOutcome(null);
        try {
            const answer = await withStepUp('DAY_END_RUN', { businessDate: day.businessDate },
                (stepUp) => api.books.dayEnd(day.businessDate, { stepUp }));
            setOutcome(runDayEndOutcome(answer));
            onStarted?.();
        } catch (e) {
            if (!(e instanceof StepUpCancelled)) setOutcome(runDayEndRefusal(e, day.businessDate));
        } finally {
            setBusy(false);
        }
    };

    // IP-203: with reconciled transactions not approved, ask first; otherwise straight to the code.
    const press = () => (warnings.confirm ? setConfirming(true) : run());

    return (
        <div className="business-day-advance">
            <div className="business-day-advance-row">
                <Tooltip title={reason}>
                    <span>
                        <Button type="primary" onClick={press} loading={busy} disabled={!enabled}>{label}</Button>
                    </span>
                </Tooltip>
                {/* IP-203: only when reviewed transactions are not reconciled yet - they move and are priced again. */}
                {warnings.info && (
                    <Tooltip title={warnings.info}>
                        <InfoCircleOutlined className="business-day-day-end-info" tabIndex={0} aria-label={warnings.info} />
                    </Tooltip>
                )}
            </div>
            <Modal
                open={confirming}
                title={'Run day end for ' + day.businessDate + '?'}
                okText="Run day end"
                cancelText="Cancel"
                onOk={() => { setConfirming(false); run(); }}
                onCancel={() => setConfirming(false)}
                rootClassName="lfoc-styles-wrapper"
                destroyOnHidden
            >
                <p>{warnings.confirm}</p>
            </Modal>
            <p className="business-day-hint">{RUN_DAY_END_HINT}</p>
            {outcome && (
                <Alert
                    type={outcome.type}
                    showIcon
                    className="business-day-outcome"
                    title={outcome.title}
                    description={outcome.lines.length ? <ul className="business-day-outcome-lines">{outcome.lines.map((line) => <li key={line}>{line}</li>)}</ul> : null}
                    closable={{ onClose: () => setOutcome(null) }}
                />
            )}
            {stepUpDialog}
        </div>
    );
};

/**
 * DAY_ADVANCE (design 5.5): runs the day's next steps now - the sync,
 * repricing and day end, as far as the facts allow, exactly as the Core does
 * by itself after each write and on its schedule. Force moves the day past
 * what forceOptions names (missing prices; statements and the funding
 * window), only with a reason, and never past pending orders. A session
 * action (core.book.close): no authenticator code. Shown only to a person
 * whose claims carry the advance key; the server checks it again.
 *
 * IP-200: on a day the Day end process owns, Advance is offered only up to
 * Reconciliation & approvals (advanceShown) - there Run day end takes over.
 */
const DayAdvance = ({ view, day, onAdvanced }) => {
    const { api, canAct } = useAccess();
    const canAdvance = canAct('businessDay', 'advance');
    const fieldId = useId();
    const [force, setForce] = useState(false);
    const [reason, setReason] = useState('');
    const [touched, setTouched] = useState(false);
    const [busy, setBusy] = useState(false);
    const [outcome, setOutcome] = useState(null);
    if (!canAdvance) return null;

    const stages = stagesOf(view);
    if (!advanceShown(day, stages)) return null;
    const closed = day.stage === STAGE.CLOSED;
    const options = forceOptions(day, { nowMs: Date.parse(view.now), stages });
    const problem = force ? reasonProblem(reason) : null;
    let forceWhy = null;
    if (closed) forceWhy = 'The day is closed';
    else if (!options.items.length) forceWhy = 'Nothing to force: Force passes only missing prices once the books are closed, and statements or the funding window once nothing is pending';

    const submit = async () => {
        if (problem) {
            setTouched(true);
            return;
        }
        setBusy(true);
        setOutcome(null);
        try {
            const answer = await api.books.advance(day.businessDate, advanceBody({ force, reason }));
            setOutcome(advanceOutcome(answer, stages));
            setForce(false);
            setReason('');
            setTouched(false);
        } catch (e) {
            setOutcome({ type: 'error', title: e.message, lines: [] });
        } finally {
            setBusy(false);
            onAdvanced?.();
        }
    };

    return (
        <div className="business-day-advance">
            <div className="business-day-advance-row">
                <Tooltip title={forceWhy}>
                    <span>
                        <Checkbox checked={force} disabled={busy || (Boolean(forceWhy) && !force)} onChange={(e) => setForce(e.target.checked)}>
                            Force
                        </Checkbox>
                    </span>
                </Tooltip>
                <Tooltip title={closed ? 'The day is closed: day end wrote its summary' : force && problem ? problem : null}>
                    <span>
                        <Button type="primary" onClick={submit} loading={busy} disabled={closed || Boolean(force && problem)}>Advance</Button>
                    </span>
                </Tooltip>
            </div>
            <p className="business-day-hint">{advanceHint(day)}</p>
            {force && (
                <div className="business-day-force">
                    <p className="business-day-hint">Force moves the day past:</p>
                    <ul className="business-day-force-list">
                        {options.items.map((item) => <li key={item}>{item}</li>)}
                    </ul>
                    <label className="business-day-field" htmlFor={fieldId}>Reason</label>
                    <Input.TextArea
                        id={fieldId}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        onBlur={() => setTouched(true)}
                        autoSize={{ minRows: 2, maxRows: 4 }}
                        maxLength={REASON_MAX}
                        showCount
                        status={touched && problem ? 'error' : undefined}
                        placeholder="Why the day moves on without it - kept with the day"
                    />
                    {touched && problem && <div className="business-day-error">{problem}</div>}
                </div>
            )}
            {options.blockedByPending && (
                <p className="business-day-hint">Day end waits for the pending transactions: Force never passes them. Approve or reject them first.</p>
            )}
            {outcome && (
                <Alert
                    type={outcome.type}
                    showIcon
                    className="business-day-outcome"
                    title={outcome.title}
                    description={outcome.lines.length ? <ul className="business-day-outcome-lines">{outcome.lines.map((line) => <li key={line}>{line}</li>)}</ul> : null}
                    closable={{ onClose: () => setOutcome(null) }}
                />
            )}
        </div>
    );
};

/**
 * One business day: its Steps in the owner's order (a stage passed over
 * marked so), its checklist and the Advance action, its funds, the stage
 * timeline with the people named, day end's summary and the forced moves.
 */
const DayPanel = ({ view, day, onAdvanced }) => {
    const stages = stagesOf(view);
    const steps = stepItems(day, stages);
    const rows = checklistRows(day, { nowMs: Date.parse(view.now), stages, view });
    const events = eventItems(day, stages);
    const forced = forcedItems(day, stages);
    const label = stageLabel(day.stage, stages);

    return (
        <div className="business-day-panel">
            <div className="business-day-head">
                <div>
                    <h3 className="panel-title">{dayLong(day.businessDate)}</h3>
                    <p className="business-day-meta">
                        {label} since {whenText(day.stageEnteredAt, day.businessDate)} · Cut-off {whenText(day.cutoffAt, day.businessDate)}
                    </p>
                </div>
                <StatusTag kind={STATUS_KIND.BUSINESS_DAY} code={day.stage} color={TONE_COLOUR[toneOf(day.stage)]} label={label} />
            </div>
            <div className="business-day-grid">
                <section className="business-day-card">
                    <h4 className="section-label">Stages</h4>
                    <Steps
                        className="business-day-steps"
                        orientation="vertical"
                        size="small"
                        current={Math.max(0, rankIn(stages, day.stage) - 1)}
                        items={steps.map((s) => ({
                            key: s.key,
                            // Each stage says what happens in it on hover or focus, as the stage tag does.
                            title: statusHelp(STATUS_KIND.BUSINESS_DAY, s.stage)
                                ? <Tooltip title={statusHelp(STATUS_KIND.BUSINESS_DAY, s.stage)}><span tabIndex={0} className="status-tag">{s.title}</span></Tooltip>
                                : s.title,
                            content: s.content,
                            status: s.status,
                            className: s.passedOver ? 'is-passed-over' : s.forced ? 'is-forced' : undefined
                        }))}
                    />
                </section>
                <section className="business-day-card">
                    <h4 className="section-label">Checklist</h4>
                    <ul className="business-day-checklist">
                        {rows.map((r) => (
                            <li key={r.key} className={r.done ? 'is-done' : r.forced ? 'is-forced' : r.warn ? 'is-warning' : 'is-waiting'}>
                                {r.done ? <CheckCircleFilled aria-label="Done" />
                                    : r.forced ? <ExclamationCircleFilled aria-label="Forced" />
                                        : r.warn ? <WarningOutlined aria-label="Warning" /> : <ClockCircleOutlined aria-label="Waiting" />}
                                <span className="business-day-check-label">{r.label}</span>
                                <span className="business-day-check-text">{r.text}</span>
                            </li>
                        ))}
                    </ul>
                    <DayEndStatus view={view} day={day} />
                    <DayAdvance key={day.businessDate} view={view} day={day} onAdvanced={onAdvanced} />
                    <DayEndRun key={'day-end-' + day.businessDate} view={view} day={day} onStarted={onAdvanced} />
                </section>
            </div>
            <section className="business-day-section">
                <h3 className="panel-title">Funds</h3>
                <Table
                    className="business-day-table"
                    rowKey="fundId"
                    size="small"
                    pagination={false}
                    scroll={{ x: 'max-content' }}
                    columns={fundColumns(day)}
                    dataSource={day.funds ?? []}
                    locale={{ emptyText: 'No fund has a book or transactions on this day' }}
                />
            </section>
            <section className="business-day-section">
                <h3 className="panel-title">Timeline</h3>
                {events.length ? (
                    <Timeline
                        className="business-day-timeline"
                        items={events.map((e) => ({
                            key: e.key,
                            color: e.forced ? 'orange' : e.closed ? 'green' : GOLD.base,
                            // The time beside the reason, not as the item's title: a title takes the drawer's left half.
                            content: (
                                <>
                                    <Text strong>{e.reason}</Text> <Text type="secondary">· {e.when}</Text>
                                    <div><Text type="secondary">{e.move} · {e.actor}</Text></div>
                                    {e.passedOver && <div><Text type="secondary">{e.passedOver}</Text></div>}
                                </>
                            )
                        }))}
                    />
                ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No moves yet" />}
            </section>
            {day.summary && (
                <section className="business-day-section">
                    <h3 className="panel-title">Day End Summary</h3>
                    <h4 className="section-label business-day-summary-label">Transactions</h4>
                    <Table
                        className="business-day-table"
                        rowKey="fundId"
                        size="small"
                        pagination={false}
                        scroll={{ x: 'max-content' }}
                        columns={summaryOrderColumns}
                        dataSource={day.summary.funds ?? []}
                        locale={{ emptyText: 'No fund dealt on this day' }}
                    />
                    <h4 className="section-label business-day-summary-label">Units and Prices</h4>
                    <Table
                        className="business-day-table"
                        rowKey="fundId"
                        size="small"
                        pagination={false}
                        scroll={{ x: 'max-content' }}
                        columns={summaryPriceColumns}
                        dataSource={day.summary.funds ?? []}
                        locale={{ emptyText: 'No fund dealt on this day' }}
                    />
                    <p className="business-day-note">{summaryNote(day.summary)}</p>
                </section>
            )}
            {forced.length > 0 && (
                <section className="business-day-section">
                    <h3 className="panel-title">Forced Moves</h3>
                    <ul className="business-day-forced">
                        {forced.map((f) => (
                            <li key={f.key}>
                                <div><Text strong>{f.stage}</Text> <Text type="secondary">· {f.when} · {f.by}</Text></div>
                                <div className="business-day-forced-reason">{f.reason}</div>
                                {f.missing && <div><Text type="secondary">Missing: {f.missing}</Text></div>}
                            </li>
                        ))}
                    </ul>
                </section>
            )}
        </div>
    );
};

/**
 * The drawer's content, for the answer of GET /books/days: the review pause
 * and the open dealing day first, then one tab per day - the live ones
 * oldest first, then the latest closed one - and a line for the days not
 * closed the read left out. Exported for the SSR tests (a closed Drawer
 * renders nothing on the server).
 *
 * @param {{ view: object|null, error?: { message: string }|null, onAdvanced?: () => void }} props
 */
export const BusinessDayContent = ({ view, error = null, onAdvanced }) => {
    if (!view) {
        return error
            ? <Alert type="error" showIcon className="business-day-alert" title={'The business day could not be read: ' + error.message} />
            : <CenteredLoader />;
    }
    const tabs = dayTabs(view);
    const paused = pauseText(view.reviewPaused);
    const openDay = openDayText(view);
    const hidden = hiddenDaysText(view);

    return (
        <div className="business-day">
            {error && <Alert type="warning" showIcon className="business-day-alert" title={'Showing the last answer. ' + error.message} />}
            {paused && (
                <Alert type="warning" showIcon icon={<PauseCircleOutlined />} className="business-day-alert" title="Reviews Paused" description={paused} />
            )}
            <div className="business-day-info">
                <ActionIcon name="info" />
                <span>
                    {openDay ?? 'The calendar holds no business day ahead yet.'} A day moves forward only, stage by stage;
                    a stage its facts went past in one move is marked passed over, and its checklist still applies.
                </span>
            </div>
            {hidden && <Alert type="info" showIcon className="business-day-alert" title={hidden} />}
            {tabs.length ? (
                <Tabs
                    className="business-day-tabs"
                    defaultActiveKey={tabs[0].key}
                    items={tabs.map((t) => ({ key: t.key, label: t.label, children: <DayPanel view={view} day={t.day} onAdvanced={onAdvanced} /> }))}
                />
            ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No business day yet" />}
        </div>
    );
};

/**
 * The "Business Day" drawer the topbar chip opens (design 8.1), over the
 * provider's one read; Refresh and every advance read it again.
 *
 * @param {{ open: boolean, onClose: () => void }} props
 */
const BusinessDayDrawer = ({ open, onClose }) => {
    const { day: view, error, refresh } = useBusinessDay();
    return (
        <Drawer
            open={open}
            onClose={onClose}
            size={720}
            title="Business Day"
            rootClassName="lfoc-styles-wrapper business-day-drawer"
            destroyOnHidden
            extra={<Button className="refresh-button" icon={<ActionIcon name="refresh" />} onClick={() => refresh?.()}>Refresh</Button>}
        >
            <BusinessDayContent view={view} error={error} onAdvanced={refresh} />
        </Drawer>
    );
};

export default BusinessDayDrawer;
